import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Box, BoxProps } from '@shopify/polaris';
import { XIcon } from '@shopify/polaris-icons';
import { MediaAsset } from '@/types/media';
import { VideoAssetSettings } from '@/features/ai-video-creator/stores/productMediaSlice';
import debounce from 'lodash.debounce';
import VideoFragmentDynamicSlider from './VideoFragmentDynamicSlider';

export const VIDEO_FRAGMENT_DURATION_SEC = 5;
export const VIDEO_FRAGMENT_DURATION_MS = VIDEO_FRAGMENT_DURATION_SEC * 1000;

const secondsToMS = (seconds: number) => Math.round(seconds * 1000);
const msToSeconds = (ms: number) => ms / 1000;

export type VideoAssetPreviewProps = {
  video: MediaAsset;
  onClose?: () => void;
  onSettingsChange?: (settings: VideoAssetSettings) => void;
} & BoxProps;

const VideoAssetPreview = ({ video, onClose, onSettingsChange, ...boxProps }: VideoAssetPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isEditMode = !!onSettingsChange;
  const videoSettings = video.settings as VideoAssetSettings | undefined;
  const originalVideoDuration = videoSettings?.duration || 0;
  const originalVideoPlayFrom = videoSettings?.playFrom || 0;
  const originalVideoPlayTill = videoSettings?.playTill || 0;

  const [startTime, setStartTime] = useState(msToSeconds(originalVideoPlayFrom));
  const [endTime, setEndTime] = useState(msToSeconds(originalVideoPlayTill || VIDEO_FRAGMENT_DURATION_MS));
  const videoDuration = videoRef.current?.duration || msToSeconds(originalVideoDuration) || endTime;

  useEffect(() => {
    const videoElement = videoRef.current;

    if (videoSettings && videoSettings.duration) {
      return;
    }

    if (videoElement && onSettingsChange) {
      const handleLoadedMetadata = () => {
        const duration = videoElement.duration;
        const defaultPlayTill = secondsToMS(Math.min(VIDEO_FRAGMENT_DURATION_SEC, duration));

        const playTill = originalVideoPlayTill || defaultPlayTill;
        const fragmentToPlay = { playFrom: originalVideoPlayFrom, playTill };

        onSettingsChange({
          ...fragmentToPlay,
          duration: secondsToMS(duration),
        });
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

      // If the video is already loaded, trigger the event handler
      if (videoElement.readyState >= 2) {
        handleLoadedMetadata();
      }

      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [video.src]);

  const debouncedHandleSliderChange = useMemo(() => {
    if (!onSettingsChange) {
      return () => {};
    }
    return debounce((newStartTime: number, newEndTime: number) => {
      setStartTime(newStartTime);
      setEndTime(newEndTime);
      onSettingsChange({
        playFrom: secondsToMS(newStartTime),
        playTill: secondsToMS(newEndTime),
        duration: secondsToMS(videoRef.current!.duration),
      });
      if (videoRef.current) {
        videoRef.current.currentTime = newStartTime;
      }
    }, 300);
  }, [onSettingsChange]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !isEditMode) return;

    const handleTimeUpdate = () => {
      const isGreaterThanRange = videoElement.currentTime >= endTime;
      const isLessThanRange = videoElement.currentTime < startTime;
      if (isGreaterThanRange || isLessThanRange) {
        videoElement.currentTime = startTime;
      }
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [startTime, endTime]);

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden">
      <div className="flex w-full flex-1 flex-col">
        <div className="relative mx-auto aspect-[9/16] flex-1 bg-black">
          <video
            key={video.src}
            ref={videoRef}
            poster={video.previewImage}
            preload={!video.previewImage ? 'metadata' : 'auto'}
            className="absolute inset-0 h-full w-full object-contain"
            controls
            muted
            autoPlay
          >
            <source src={`${video.src}${video.previewImage ? '' : '#t=0.1'}`} />
          </video>
        </div>
      </div>
      <div className="w-full">
        {onSettingsChange &&
          (videoDuration ? (
            <VideoFragmentDynamicSlider
              initialStartTime={startTime}
              initialEndTime={endTime}
              duration={videoDuration}
              onSliderChange={debouncedHandleSliderChange}
            />
          ) : (
            <div className="h-10 w-full animate-pulse rounded bg-slate-200"></div>
          ))}
      </div>
      {onClose && (
        <Box position="absolute" insetInlineEnd="400" insetBlockStart="400">
          <Button icon={XIcon} onClick={onClose} variant="plain" accessibilityLabel="Close preview" />
        </Box>
      )}
    </div>
  );
};

export default memo(VideoAssetPreview);
